import { BOOKING_TAG_FILTER_OPTIONS } from "@/lib/bookingTagFilterOptions";
import { BOOKING_STATE_LABELS, type BookingState } from "@/types/booking";

export const TOKEN_KEYS = [
  "state",
  "state_not",
  "carrier",
  "carrier_not",
  "agent",
  "agent_not",
  "origin_port",
  "origin_port_not",
  "destination_port",
  "destination_port_not",
  "contract_ref",
  "contract_ref_not",
  "shipper",
  "shipper_not",
  "consignee",
  "consignee_not",
  "sales_person",
  "sales_person_not",
  "opened_by",
  "opened_by_not",
  "week",
  "week_not",
  "service_trade",
  "service_trade_not",
  "tag",
  "tag_not",
  "equipment_size",
  "equipment_type",
  "delay_source",
  "eta_delay_source",
] as const;

export const DATE_KEYS = [
  "departure_from",
  "departure_to",
  "eta_from",
  "eta_to",
  "pcd_from",
  "pcd_to",
  "cutoff_from",
  "cutoff_to",
  "stuffing_from",
  "stuffing_to",
  "ready_date_from",
  "ready_date_to",
  "open_date_from",
  "open_date_to",
] as const;

export const WEIGHT_KEYS = [
  "weight_gte",
  "weight_lte",
  "weight_eq",
  "total_containers_gte",
  "total_containers_lte",
  "total_containers_eq",
  "delay_days_gte",
  "delay_days_lte",
  "delay_days_eq",
  "eta_delay_days_gte",
  "eta_delay_days_lte",
  "eta_delay_days_eq",
] as const;

export const STATE_OPTIONS = (
  Object.entries(BOOKING_STATE_LABELS) as [BookingState, string][]
).map(([value, label]) => ({ value, label }));

/** Same tag enum as the bookings list filters (includes document-intel tags). */
export const TAG_OPTIONS = BOOKING_TAG_FILTER_OPTIONS;

export const EQUIPMENT_SIZE_OPTIONS = [
  { value: "20", label: "20'" },
  { value: "40", label: "40'" },
  { value: "45", label: "45'" },
];

export const EQUIPMENT_TYPE_OPTIONS = [
  { value: "DV", label: "DV (Dry Van)" },
  { value: "HC", label: "HC (High Cube)" },
  { value: "Reefer", label: "Reefer" },
  { value: "OT", label: "OT (Open Top)" },
  { value: "FR", label: "FR (Flat Rack)" },
  { value: "TK", label: "TK (Tank)" },
  { value: "DG", label: "DG (Dangerous Goods)" },
  { value: "OOG", label: "OOG (Out of Gauge)" },
];

export const DELAY_SOURCE_OPTIONS = [
  { value: "carrier_tracking", label: "Carrier tracking" },
  { value: "agent", label: "Agent" },
  { value: "schedule", label: "Schedule" },
];

export const DATE_WINDOW_OPTIONS = [
  { value: "", label: "Custom dates" },
  { value: "this_week", label: "This week" },
  { value: "last_week", label: "Last week" },
  { value: "next_week", label: "Next week" },
  { value: "this_month", label: "This month" },
  { value: "next_month", label: "Next month" },
  { value: "next_30_days", label: "Next 30 days" },
  { value: "next_60_days", label: "Next 60 days" },
  { value: "next_90_days", label: "Next 90 days" },
];

export const BACKWARD_WINDOW_OPTIONS = [
  { value: "", label: "Custom / off" },
  { value: "this_week", label: "This week" },
  { value: "last_week", label: "Last week" },
  { value: "this_month", label: "This month" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_14_days", label: "Last 14 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_90_days", label: "Last 90 days" },
];

export const SORT_DIR_OPTIONS = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
];

export const EXPOSE_ROLE_OPTIONS = [
  { value: "", label: "Anyone in the organization" },
  { value: "Client", label: "Consignees and above" },
  { value: "SalesPerson", label: "Sales and above" },
  { value: "User", label: "Operations and above" },
  { value: "Finance", label: "Finance and above" },
  { value: "Admin", label: "Admins and above" },
  { value: "SystemAdmin", label: "System admins only" },
];

/** Dashboard visibility gates — ordered least → most privileged (mirrors core). */
export const DASHBOARD_VIEWER_ROLE_OPTIONS = [
  { value: "", label: "Everyone in the organization" },
  { value: "Client", label: "Consignees only" },
  { value: "SalesPerson", label: "Sales team" },
  { value: "User", label: "Operations (users)" },
  { value: "Finance", label: "Finance team" },
  { value: "Admin", label: "Admins only" },
] as const;

export const COMMON_TIMEZONES = [
  "UTC",
  "US/Eastern",
  "US/Central",
  "US/Mountain",
  "US/Pacific",
  "Europe/London",
  "Europe/Amsterdam",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Rome",
  "Europe/Athens",
  "Europe/Istanbul",
  "Asia/Jerusalem",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Auckland",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "Africa/Johannesburg",
  "Africa/Cairo",
];

export const WEEKDAY_OPTIONS = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

/** Mirrors tideway-core `MemberRole::privilege_level`. */
const ROLE_PRIVILEGE: Record<string, number> = {
  client: 0,
  consignee: 0,
  salesperson: 1,
  sales_person: 1,
  sales: 1,
  user: 2,
  viewer: 2,
  finance: 3,
  admin: 10,
  operator: 10,
  systemadmin: 20,
};

export function rolePrivilege(role: string | null | undefined): number {
  if (!role) return 0;
  return ROLE_PRIVILEGE[role.trim().toLowerCase()] ?? 0;
}

export function minRoleLabel(role: string): string {
  switch (role) {
    case "Client":
      return "Consignee";
    case "SalesPerson":
      return "Sales";
    case "User":
      return "Operations";
    case "Finance":
      return "Finance";
    case "Admin":
      return "Admin";
    case "SystemAdmin":
      return "System admin";
    default:
      return "Operations";
  }
}

export function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

export function timezoneLabel(tz: string): string {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    });
    const offset =
      formatter.formatToParts(new Date()).find((p) => p.type === "timeZoneName")
        ?.value ?? "";
    return `${tz.replace(/_/g, " ")} (${offset})`;
  } catch {
    return tz;
  }
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function startOfIsoWeek(d: Date): Date {
  const monday = new Date(d);
  const day = monday.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  monday.setDate(monday.getDate() + offset);
  return monday;
}

function addDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

/** Preview of a forward ETD/ETA window, mirroring backend `compute_date_window`. */
export function previewDateWindow(window: string): string | null {
  if (!window) return null;
  const today = new Date();
  switch (window) {
    case "this_week": {
      const monday = startOfIsoWeek(today);
      return `${fmtDate(monday)} – ${fmtDate(addDays(monday, 6))}`;
    }
    case "last_week": {
      const monday = addDays(startOfIsoWeek(today), -7);
      return `${fmtDate(monday)} – ${fmtDate(addDays(monday, 6))}`;
    }
    case "next_week": {
      const monday = addDays(startOfIsoWeek(today), 7);
      return `${fmtDate(monday)} – ${fmtDate(addDays(monday, 6))}`;
    }
    case "this_month": {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return `${fmtDate(first)} – ${fmtDate(last)}`;
    }
    case "next_month": {
      const first = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return `${fmtDate(first)} – ${fmtDate(last)}`;
    }
    case "next_30_days":
    case "next_60_days":
    case "next_90_days": {
      const days = window === "next_30_days" ? 30 : window === "next_60_days" ? 60 : 90;
      const to = new Date(today);
      to.setDate(to.getDate() + days);
      return `${fmtDate(today)} – ${fmtDate(to)}`;
    }
    default:
      return null;
  }
}

/** Preview of a backward last-updated / open-date window. */
export function previewLastUpdatedWindow(window: string): string | null {
  if (!window) return null;
  const today = new Date();
  const back = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return d;
  };
  switch (window) {
    case "this_week":
    case "last_week":
    case "this_month":
      return previewDateWindow(window);
    case "today":
      return fmtDate(today);
    case "yesterday":
      return fmtDate(back(1));
    case "last_7_days":
      return fmtDate(back(7));
    case "last_14_days":
      return fmtDate(back(14));
    case "last_30_days":
      return fmtDate(back(30));
    case "last_90_days":
      return fmtDate(back(90));
    default:
      return null;
  }
}

export function parsePositiveInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n > 0 ? n : null;
}
